<template>
  <div v-show="true">
    <div v-if="isLoading" class="flex justify-center items-center space-x-2 mt-8 border border-gray-200 py-4 px-5 w-full text-lg rounded-full">
      <div class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status"></div>
      <div class="text-lg text-gray-500">Loading activities, please wait...</div>
    </div>

    <div v-if="recentActivities.length > 0">
      <div class="flex flex-col space-y-4 mt-6 mb-6">
        <div v-for="activity in recentActivities" 
             :class="{
              'bg-pink-50': activity.action !== 'campaign_created' && activity.action !== 'honoree_donation_created',
              'bg-purple-100': activity.action === 'honoree_donation_created',
              'bg-blue-50': activity.action === 'campaign_created',
              'md:items-center': activity.action !== 'honoree_donation_created',
              'md:items-start': activity.action === 'honoree_donation_created'
             }" 
             class="w-full text-lg flex flex-col md:flex-row activity-class rounded-3xl pr-6 pl-20 md:pr-8 md:pl-16 py-4 relative">
          <!-- Icon and Action Left Panel -->
          <div class="absolute -left-5 md:-left-8 -top-1 md:-top-[5px]" :class="{ 'md:-top-0.5': activity.action === 'honoree_donation_created' }">
            <div v-if="activity.action == 'donation_created'" class="bg-pink text-white rounded-full h-20 w-20 mr-6 flex items-center justify-center text-2xl  border-solid border-4 border-white">
              <!-- Donation Icon -->
              <img class="block w-3/5" src="/img/donation-icon.png">
            </div>
            <div v-if="activity.action == 'honoree_donation_created'" class="bg-purple text-white rounded-full h-20 w-20 mr-6 flex items-center justify-center text-2xl  border-solid border-4 border-white">
              <!-- Honor Icon -->
              <img class="block w-3/5" src="/img/honor-icon.png">
            </div>
            <!-- Additional activities' icons similar to the All Tab -->
          </div>

          <!-- Activity Details -->
          <div class="flex-grow md:pr-8">
            <!-- Donation Created Activity -->
            <!-- Donation Created Activity with Different Campaign ID -->
            <div v-if="activity.action == 'donation_created' && activity.sub_campaign_id != activity.campaign_id" class="flex flex-col text-base text-black leading-tight">
              <p>
                <strong>{{ activity.subject || 'An anonymous donor' }}</strong> donated to 
                
                <span class="md:block" v-if="activity.sub_campaign_id != campaignId">
                  <a class="text-pink hover:text-pink-600" :href="campaignUrl(activity.sub_campaign_id)">
                    <strong>{{ activity.campaignName }} campaign</strong>
                  </a>
                </span>

                <span class="md:block" v-if="activity.sub_campaign_id == campaignId">
                  <strong class="text-pink hover:text-pink-600">{{ activity.campaignName }} campaign</strong>
                </span>
              </p>
            </div>

            <!-- Donation Created Activity with Same Campaign ID -->
            <div v-else-if="activity.action == 'donation_created' && activity.sub_campaign_id == activity.campaign_id" class="flex flex-col text-base text-black leading-tight">
              <p>
                <strong>{{ activity.subject || 'An anonymous donor' }}</strong> donated to 
                
                <span class="md:block" v-if="activity.sub_campaign_id == campaignId">
                  <strong class="text-pink hover:text-pink-600">{{ activity.campaignName }} campaign</strong>
                </span>
              </p>
            </div>

            <!-- Honoree Donation Created Activity -->
            <div v-else-if="activity.action == 'honoree_donation_created'" class="flex flex-col text-base text-black leading-tight">
              <p>
                <strong>{{ activity.subject || 'An anonymous donor' }}</strong> donated 
                <span v-if="activity.dedication === 'in_honor_of'">in honor of</span>
                <span v-else-if="activity.dedication === 'in_memory_of'">in memory of</span>
                
                <span class="md:block mb-3">
                  <strong class="text-purple hover:text-purple-600 md:block">{{ activity.sub_message }}</strong>
                </span>
              </p>
            </div>

            <!-- Other Activities (Excluding Campaign Created) -->
            <p v-else class="text-base text-gray-500 leading-tight">
              <strong>{{ activity.subject }}</strong> {{ activity.message }}
            </p>

            <p v-if="activity.action != 'campaign_created'" class="text-base text-gray-500 leading-tight italic mt-3 md:mt-0">
              {{ getMessageToRecipient(activity) }}
            </p>
          </div>

          <div v-if="activity.action == 'donation_created' || activity.action == 'honoree_donation_created'" class="flex flex-col items-start text-base leading-tight w-20 mt-3 md:mt-0">
            <span>Amount</span>
            <span :class="{'text-pink font-bold': activity.action == 'donation_created', 'text-purple font-bold': activity.action == 'honoree_donation_created'}">
              {{ activity.donationAmount }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More Button - Remaining Same -->
    <a v-if="next_page_url" href="#!" @click="loadMore($event)" id="loadMore"
      :disabled="isLoadMoreDisabled"
      class="mb-3 bg-white border-2 border-blue hover:border-blue-700 hover:text-blue-700 text-blue block py-3 font-medium uppercase text-center text-lg mt-10 focus:outline-none focus:ring-blue-200 rounded-full transform transition-all duration-500 w-3/4 lg:w-1/2 mx-auto">
      <div class="flex items-center justify-center relative">
        <span class="text-center mx-auto">Load More</span>
        <div class="spinner-container">
          <div v-if="isLoadingMore" class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status"></div>
        </div>
      </div>
    </a>

    <div v-else-if="!isLoading && recentActivities.length <= 0" class="mb-6">
      <div class="flex flex-col mt-6 space-y-5">
        <div class="border border-gray-200 py-4 px-5 w-full text-lg flex items-center">
          <p>Be the first to <a href="#" class="text-blue hover:text-blue-600 rounded-full">donate</a> and support our cause.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: 'DonorActivities',

  props: {
    campaignId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      showDonors: false,
      isLoading: true,
      isLoadingMore: false,
      recentActivities: [],
      next_page_url: '/api/open/campaigns/activity/' + this.campaignId,
      isLoadMoreDisabled: false,
    }
  },

  created() {
    this.isLoading = true
    this.isLoadingMore = false
    this.loadActivities()

    this.emitter.on('campaign-donation-update', (campaign) => {
      this.campaign = campaign
      this.next_page_url = '/api/open/campaigns/activity/' + this.campaignId
      this.recentActivities = []
      this.loadActivities()
    })
  },

  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return '';

      let number = parseFloat(amount);
      if (isNaN(number)) {
        return amount;
      }

      return `$${number.toFixed(2)}`;
    },
    getDonationAmount(message) {
      const amountRegex = /\$\d+(?:\.\d{0,2})?/;
      const matches = message.match(amountRegex);

      if (matches && matches.length > 0) {
        let donationAmount = matches[0];
        if (!donationAmount.includes('.')) {
          donationAmount += '.00';
        }
        return donationAmount;
      } else {
        return null;
      }
    },
    getMessageToRecipient(activity) {
      return _.get(activity.meta_data, 'message_to_recipient', null)
    },

    loadMore(event) {
      event.preventDefault();
      this.isLoadMoreDisabled = true;
      this.isLoadingMore = true;
      this.loadActivities().then(() => {
        this.isLoadMoreDisabled = false;
        this.isLoadingMore = false;
      });
    },

    async fetchDonationDetails(activity) {
      try {
        const response = await axios.get(`/api/open/donations/${activity.donation_id}`);
        const donation = response.data;

        activity.donationAmount = this.formatCurrency(donation.amount);
        activity.isAnonymous = donation.is_anonymous;
        activity.dedication = donation.dedication;
        activity.inMemoryHonorOf = donation.in_memory_honor_of;
        activity.honoreeName = donation.honoree_name;
        return activity;
      } catch (error) {
        console.error('Error fetching donation details:', error);
        return activity;
      }
    },

    async loadActivities() {
      if (this.next_page_url) {
        try {
          const response = await axios.get(this.next_page_url, {params: {activity: 'donation'}});

          const activitiesWithDetails = response.data.data.map(activity => {
            if (activity.action === 'honoree_donation_created' || activity.action === 'donation_created') {
              return this.fetchDonationDetails(activity);
            }
            return activity;
          });

          const resolvedActivities = await Promise.all(activitiesWithDetails);

          this.recentActivities.push(...resolvedActivities);
          this.next_page_url = response.data.next_page_url;
        } catch (error) {
          console.error('Error loading activities:', error);
        }
      }

      this.isLoading = false;
      this.isLoadingMore = false;
    },

    async fetchDonationDetails(activity) {
      try {
        const response = await axios.get(`/api/open/donations/${activity.donation_id}`);
        const donation = response.data;

        activity.donationAmount = this.formatCurrency(donation.amount);
        activity.isAnonymous = donation.is_anonymous;
        activity.dedication = donation.dedication;
        activity.inMemoryHonorOf = donation.in_memory_honor_of;
        activity.honoreeName = donation.honoree_name;
        return activity;
      } catch (error) {
        console.error('Error fetching donation details:', error);
        return activity;
      }
    },

    campaignUrl(id) {
      return `/campaigns/${id}`
    },
  },
}
</script>
<style scoped>
.st0{
  fill:#FFFFFF;
}
.spinner-border {
  border-color: transparent;
  border-top-color: #3490dc; /* Spinner color */
  border-right-color: #3490dc; /* Spinner color */
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
.amount-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
}

.activities-container {
  max-width: 768px; /* Adjust this width as needed */
  margin: 0 auto; /* Center the container */
  width: 100%;
}

.spinner-space {
  width: 2rem; /* Same as spinner width */
  height: 2rem; /* Same as spinner height */
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-container {
  position: absolute;
  right: 1rem;
  width: 2rem; /* Same as spinner width */
  height: 2rem; /* Same as spinner height */
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-border {
  border-color: transparent;
  border-top-color: #3490dc; /* Spinner color */
  border-right-color: #3490dc; /* Spinner color */
}
</style>
